import { Box, Checkbox, Tooltip } from '@timelyapp/react';
import DropdownListItem from 'components/DropdownListItem';
import { BorderlessStar } from 'components/Icons';
import { AGENT_REPORT_COLUMNS } from 'constants/agentReporting';
import * as constants from 'constants/filters';
import { WIDGET_CLIENTS_LIST, WIDGET_TEAMS_LIST, WIDGET_USERS_HBAR } from 'constants/widgets';
import { columnNameFactory } from 'factories/superreports';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useMemo } from 'react';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import type { WidgetWithData } from 'redux/modules/superreports.types';
import type { Features } from 'types/account';
import { useHandleChangeColumns } from './helpers';

const individualEntryColumns: Array<constants.Column> = [
  constants.COLUMN_DATE,
  constants.COLUMN_TIMESTAMPS,
  constants.COLUMN_TAGS,
  constants.COLUMN_NOTES,
];

const columnGroups: Array<[subheading: string, columns: Array<constants.Column>]> = [
  [
    'Logged',
    [
      constants.COLUMN_LOGGED_HOURS,
      constants.COLUMN_LOGGED_PERCENTAGE,
      constants.COLUMN_LOGGED_MONEY,
      constants.COLUMN_INTERNAL_COST,
      constants.COLUMN_PROFIT,
      constants.COLUMN_PROFITABILITY,
    ],
  ],
  [
    'Billed & unbilled',
    [
      constants.COLUMN_BILLED_HOURS,
      constants.COLUMN_BILLED_MONEY,
      constants.COLUMN_UNBILLED_HOURS,
      constants.COLUMN_UNBILLED_MONEY,
    ],
  ],
  ['Planned', [constants.COLUMN_ESTIMATED_HOURS, constants.COLUMN_ESTIMATED_MONEY]],
  ['Billable', [constants.COLUMN_BILLABLE_HOURS, constants.COLUMN_NON_BILLABLE_HOURS]],
  ['Budget', [constants.COLUMN_BUDGET, constants.COLUMN_BUDGET_REMAINING]],
  ['Capacity', [constants.COLUMN_CAPACITY, constants.COLUMN_OVERTIME, constants.COLUMN_UNDERTIME]],
  ['Calculated', [constants.COLUMN_UTILIZATION]],
  ['Individual entries', individualEntryColumns],
  ['Agent work', [...AGENT_REPORT_COLUMNS]],
];

interface Props {
  billedState: boolean | null;
  features: Features;
  widget: WidgetWithData;
}

const TableWidgetOptions: React.FC<Props> = ({ billedState, features, widget }) => {
  const reference = (widget.id || widget._ref)!;
  const columns = widget.columns.names;
  const handleChange = useHandleChangeColumns(columns, reference, widget._status?.expandedRowIds);
  const actions = useWithDispatch({ showSubscriptionNoticeModal });

  const availableColumns = useMemo(() => {
    const filters: Array<string> = [];
    if (!features.agent_tracking) filters.push('Agent work');

    if (widget.widget_type !== WIDGET_CLIENTS_LIST) {
      filters.push('Budget');
    }

    if (billedState !== null) {
      filters.push('Billed & unbilled');
    }

    if (
      !features.capacity_reports ||
      ![WIDGET_USERS_HBAR, WIDGET_TEAMS_LIST].includes(widget.widget_type)
    ) {
      // Utilization divides billable hours by capacity, so it goes wherever capacity goes
      filters.push('Capacity', 'Calculated');
    }

    return filters.length > 0 ? columnGroups.filter(([k]) => !filters.includes(k)) : columnGroups;
  }, [billedState, features.capacity_reports, features.agent_tracking, widget.widget_type]);

  const hasAccessToColumn = useCallback(
    (column: constants.Column) =>
      Object.entries(constants.FEATURE_GATED_COLUMNS).every(([feature, columns]) =>
        columns.includes(column) ? features[feature as keyof Features] : true
      ),
    [features]
  );

  const handleShowUpgradeModal = useCallback(
    (column: constants.Column) => {
      switch (column) {
        case constants.COLUMN_INTERNAL_COST:
          actions.showSubscriptionNoticeModal('featureLoggedCostRequiresUpgrade');
          break;
        case constants.COLUMN_PROFIT:
          actions.showSubscriptionNoticeModal('featureProfitRequiresUpgrade');
          break;
        case constants.COLUMN_PROFITABILITY:
          actions.showSubscriptionNoticeModal('featureProfitabilityRequiresUpgrade');
          break;
      }
    },
    [actions]
  );

  return (
    <>
      <DropdownListItem key="content-header" type="header">
        {features.agent_tracking ? 'Human work' : 'Content'}
      </DropdownListItem>
      {availableColumns.flatMap(([subheading, groupColumns], index) =>
        [
          index > 0 ? <DropdownListItem key={`divider-${index}`} type="divider" /> : undefined,
          <DropdownListItem
            key={`subheading-${index}`}
            type={
              subheading === 'Agent work' || subheading === 'Individual entries'
                ? 'header'
                : 'muted'
            }
          >
            {subheading}
          </DropdownListItem>,
        ].concat(
          groupColumns.map(column => {
            const isChecked = columns.includes(column);
            const columnName =
              constants.WIDGET_OPTION_COLUMN_LABELS[column] ??
              columnNameFactory(column, widget.widget_type);

            if (!hasAccessToColumn(column)) {
              return (
                <DropdownListItem key={column} onClick={() => handleShowUpgradeModal(column)}>
                  <Tooltip
                    tooltip="This feature is not available. Click to learn more."
                    position="bottom"
                    usePortal
                  >
                    <Box.Row gap={10}>
                      <BorderlessStar /> {columnName}
                    </Box.Row>
                  </Tooltip>
                </DropdownListItem>
              );
            }

            return (
              <DropdownListItem key={column} onClick={() => handleChange(column, !isChecked)}>
                <Checkbox
                  id={`${reference}-${column}`}
                  onChangeChecked={checked => handleChange(column, checked)}
                  checked={isChecked}
                  label={columnName}
                />
              </DropdownListItem>
            );
          })
        )
      )}
    </>
  );
};

export default TableWidgetOptions;
